import { productsLikeCreate, productsMyLikesList } from "@innoclouds/api";
import { defineStore } from "pinia";
import { computed, reactive, watch } from "vue";
import { useAuth } from "#/composables/useAuth";
import { useMainStore } from "#/store/main";
import { useUserStore } from "#/store/user";
import { emitter } from "#/utils/emitter";

export const useLikesStore = defineStore("likes", () => {
	const counts = reactive(new Map<string, number>());
	const likedByMe = reactive(new Set<string>());
	const pendingIds = reactive(new Set<string>());
	const mainStore = useMainStore();
	const { accessToken } = useAuth();
	const isEnabled = computed(() => mainStore.config_lib?.display_likes === true);

	const refresh = async () => {
		const likes = await productsMyLikesList();
		likedByMe.clear();
		for (const like of likes) {
			counts.set(like.product_id, like.likes_count);
			if (like.liked !== false) likedByMe.add(like.product_id);
		}
	};

	const setCount = (id: string, count: number) => {
		counts.set(id, count);
	};

	const getCount = (id: string) => counts.get(id) ?? 0;

	const isLiked = (id: string) => likedByMe.has(id);
	const isPending = (id: string) => pendingIds.has(id);

	const toggleLike = async (id: string) => {
		if (!isEnabled.value || pendingIds.has(id)) return;

		pendingIds.add(id);
		const wasLiked = likedByMe.has(id);
		const previousCount = counts.get(id) ?? 0;
		const liked = !wasLiked;

		try {
			await productsLikeCreate({ product_id: id, liked });
			if (liked) {
				likedByMe.add(id);
				counts.set(id, previousCount + 1);
			} else {
				likedByMe.delete(id);
				counts.set(id, Math.max(0, previousCount - 1));
			}
			emitter.emit("like-toggled", { productId: id, liked });
		} catch {
		} finally {
			pendingIds.delete(id);
		}
	};

	// Сессия гостя закончилась (киоск после заказа) — лайки могли поменяться на бэкенде.
	// С токеном это и так сделает watch ниже, без токена он не сработает
	useUserStore().$onAction(({ name, after }) => {
		if (name !== "logout" || !isEnabled.value || accessToken.value) return;
		after(() => void refresh().catch(() => undefined));
	});

	watch(
		[isEnabled, accessToken],
		([enabled]) => {
			if (enabled) void refresh().catch(() => undefined);
			else {
				counts.clear();
				likedByMe.clear();
			}
		},
		{ immediate: true },
	);

	return {
		counts,
		likedByMe,
		pendingIds,
		isEnabled,
		refresh,
		setCount,
		getCount,
		isLiked,
		isPending,
		toggleLike,
	};
});
